/**
 * Posições da HUD guardadas no navegador (techspec-hud-v3 §02 e §06): onde o jogador deixou cada janela
 * e cada botão flutuante, por personagem e por modo de tela (PC e tablet têm arranjos próprios).
 *
 * É só preferência visual. localStorage pode faltar (aba privada, sem permissão, cota): toda leitura e
 * gravação fica em try/catch e, sem ele, tudo volta ao padrão.
 */
import type { ModoTela } from './useModoTela';

export type PosJanela = { x: number; y: number };
export type CasaBotao = [number, number];

let dono = '';
const ouvintes = new Set<() => void>();

/** O personagem que está jogando; as posições de um não valem para o outro. */
export function definirDonoPosicoes(personagem: string) {
  dono = personagem.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 64);
}

/** Só PC e tablet guardam: no celular as janelas são folhas e os botões têm a grade fixa. */
const guardaModo = (modo: ModoTela) => modo === 'pc' || modo === 'tablet';
/** `botoes2`: a grade dos botões passou a ter meio passo (BotoesFlutuantes, SUB = 2). A chave `botoes` é a antiga. */
const chave = (modo: ModoTela, tipo: 'janelas' | 'botoes' | 'botoes2') => `uoidle.hud.${dono || 'anon'}.${modo}.${tipo}`;
const SUB_GRADE = 2;

function ler<T>(k: string): Record<string, T> {
  try {
    const v = JSON.parse(localStorage.getItem(k) ?? 'null');
    return v && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, T> : {};
  } catch {
    return {};
  }
}
function gravar(k: string, v: unknown) {
  try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* sem storage: fica o padrão */ }
}

const numero = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

export function lerJanela(modo: ModoTela, janela: string): PosJanela | null {
  if (!guardaModo(modo) || !janela) return null;
  const p = ler<PosJanela>(chave(modo, 'janelas'))[janela];
  return p && numero(p.x) && numero(p.y) ? { x: p.x, y: p.y } : null;
}

export function gravarJanela(modo: ModoTela, janela: string, pos: PosJanela) {
  if (!guardaModo(modo) || !janela) return;
  const k = chave(modo, 'janelas');
  gravar(k, { ...ler(k), [janela]: { x: Math.round(pos.x), y: Math.round(pos.y) } });
}

/** Modo cel usa a chave dele também: o arranjo dos botões no celular pode ser mexido. */
export function lerBotoes(modo: ModoTela): Record<string, CasaBotao> {
  let salvo = ler<CasaBotao>(chave(modo, 'botoes2'));
  let fator = 1;
  // Quem arrumou os botões na grade antiga: a mesma casa vale o dobro na grade de meio passo.
  if (Object.keys(salvo).length === 0) {
    salvo = ler<CasaBotao>(chave(modo, 'botoes'));
    fator = SUB_GRADE;
  }
  const out: Record<string, CasaBotao> = {};
  for (const [id, c] of Object.entries(salvo)) {
    if (Array.isArray(c) && c.length === 2 && Number.isInteger(c[0]) && Number.isInteger(c[1]) && c[0] >= 0 && c[1] >= 0)
      out[id] = [c[0] * fator, c[1] * fator];
  }
  return out;
}

export function gravarBotoes(modo: ModoTela, pos: Record<string, CasaBotao>) {
  gravar(chave(modo, 'botoes2'), pos);
}

/** Configurações › Interface › Restaurar posições: apaga janelas e botões do personagem em todos os modos. */
export function restaurarPosicoes() {
  for (const modo of ['pc', 'tablet', 'cel', 'deitado'] as ModoTela[]) {
    for (const tipo of ['janelas', 'botoes', 'botoes2'] as const) {
      try { localStorage.removeItem(chave(modo, tipo)); } catch { /* sem storage */ }
    }
  }
  ouvintes.forEach(f => f());
}

/** Quem precisa voltar ao padrão quando o jogador restaura (janelas abertas, botões). */
export function aoRestaurar(f: () => void) {
  ouvintes.add(f);
  return () => { ouvintes.delete(f); };
}
